/**
 * Score Delta Calculator Utility
 * Provides functions to calculate and display score deltas for questions
 */

import { QUESTIONS, QuestionChoice } from '@/data/gameGuideContent';

export interface ScoreDelta {
  brain: number;
  heart: number;
  task: number;
  comm: number;
}

/**
 * Get the score delta for a specific question and answer
 */
export function getScoreDelta(questionId: string, answerId: string): ScoreDelta | null {
  const question = QUESTIONS.find(q => q.id === questionId);
  if (!question) return null;

  const choice = question.choices.find(c => c.id === answerId);
  if (!choice) return null;

  return {
    brain: choice.scores.brain,
    heart: choice.scores.heart,
    task: choice.scores.task,
    comm: choice.scores.comm
  };
}

/**
 * Get the question text for a given question ID
 */
export function getQuestionText(questionId: string): string {
  const question = QUESTIONS.find(q => q.id === questionId);
  return question?.text || questionId;
}

/**
 * Get the answer text for a given question and answer ID
 */
export function getAnswerText(questionId: string, answerId: string): string {
  const question = QUESTIONS.find(q => q.id === questionId);
  if (!question) return String(answerId);

  const choice = question.choices.find(c => c.id === answerId);
  return choice?.text || String(answerId);
}

/**
 * Get the full choice object for a question and answer
 */
export function getChoice(questionId: string, answerId: string): QuestionChoice | null {
  const question = QUESTIONS.find(q => q.id === questionId);
  if (!question) return null;

  return question.choices.find(c => c.id === answerId) || null;
}

/**
 * Check if a question is a text input question (no score deltas)
 */
export function isTextQuestion(questionId: string): boolean {
  const question = QUESTIONS.find(q => q.id === questionId);
  return question?.type === 'text';
}

/**
 * Get the stage number for a question
 */
export function getQuestionStage(questionId: string): number | null {
  const question = QUESTIONS.find(q => q.id === questionId);
  return question?.stage ?? null;
}

/**
 * Calculate total score from a delta
 */
export function calculateDeltaTotal(delta: ScoreDelta): number {
  return delta.brain + delta.heart + delta.task + delta.comm;
}

/**
 * Check if a score delta has any non-zero values
 */
export function hasScoreImpact(delta: ScoreDelta | null): boolean {
  if (!delta) return false;
  return delta.brain !== 0 || delta.heart !== 0 || delta.task !== 0 || delta.comm !== 0;
}
